import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  if (!category) {
    return (
      <>
        <SEO title="分类未找到" description="抱歉，您查找的分类不存在。" />
        <Navbar />
        <main className="min-h-screen pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">
              分类未找到
            </h1>
            <Link
              to="/"
              className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 韩国伦理文化研究`,
    description: category.description,
    url: window.location.href,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国伦理文化研究',
      url: 'https://korea-ethics.com/',
    },
  };

  return (
    <>
      <SEO
        title={`${category.name} - 韩国伦理文化分类`}
        description={category.description}
        keywords={`韩国伦理,${category.name},韩国伦理文化,韩国社会伦理`}
        canonicalUrl={`https://korea-ethics.com/category/${slug}`}
        schema={pageSchema}
      />

      <Navbar />
      <ScrollToTop />

      <main className="min-h-screen pt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category.name },
            ]}
          />

          <div className="relative rounded-2xl overflow-hidden mb-10 h-48 md:h-64">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url('${category.imageUrl}')` }}
            ></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-black/20"></div>
            <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-10">
              <div className="w-12 h-12 mb-4 rounded-xl bg-background-50/20 backdrop-blur-sm flex items-center justify-center">
                <i className={`${category.icon} text-xl text-background-50`}></i>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
                {category.name}
              </h1>
              <p className="text-background-50/80 text-sm max-w-lg">{category.description}</p>
              <span className="text-background-50/60 text-xs mt-2">
                共 {category.articleCount} 篇文章
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            <Link
              to="/"
              className="px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {categoryArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 cursor-pointer transition-all duration-300 hover:-translate-y-0.5"
              >
                <div className="h-48 relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-500"
                    style={{ backgroundImage: `url('${article.imageUrl}')` }}
                  ></div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs text-foreground-400">{article.date}</span>
                    <span className="text-xs text-foreground-400">{article.readTime} 分钟</span>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}